// src/pages/AdminCompTickets.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api"; // shared axios client

// how many comps we let the MASTER issue in one go
const MAX_COMP_TICKETS = 10;

// reuse same badge idea
const Badge = ({ children, tone = "default" }) => {
  const cls =
    tone === "success"
      ? "bg-emerald-500/20 text-emerald-200"
      : tone === "warning"
      ? "bg-amber-500/20 text-amber-200"
      : "bg-white/10 text-slate-200";
  return <span className={`badge ${cls}`}>{children}</span>;
};

const EMPTY_HOLDER = {
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  ticketType: "STANDARD",
};

export default function AdminCompTickets() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);

  // form state
  const [eventId, setEventId] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState("");
  const [tickets, setTickets] = useState([{ ...EMPTY_HOLDER }]);

  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("DRAFT"); // DRAFT | SENT
  const [message, setMessage] = useState("");
  const [reservationToken, setReservationToken] = useState("");

  // load events (same as other admin pages)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/events/list");
        const filtered = (data || []).filter((ev) => !ev.cancelled);
        setEvents(filtered);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingEvents(false);
      }
    })();
  }, []);

  // allow up to 10
  const canAddMore = tickets.length < MAX_COMP_TICKETS;

  const addHolder = () => {
    if (!canAddMore) return;
    setTickets((prev) => [...prev, { ...EMPTY_HOLDER }]);
  };

  const removeHolder = (i) => {
    setTickets((prev) => prev.filter((_, idx) => idx !== i));
  };

  const updateHolder = (i, key, value) => {
    setTickets((prev) =>
      prev.map((t, idx) => (idx === i ? { ...t, [key]: value } : t))
    );
  };

  const isValid = useMemo(() => {
    if (!eventId) return false;
    if (!recipientEmail || !recipientEmail.includes("@")) return false;
    if (tickets.length === 0 || tickets.length > MAX_COMP_TICKETS) return false;
    for (const t of tickets) {
      if (!t.firstName.trim()) return false;
      if (!t.lastName.trim()) return false;
      if (!t.dateOfBirth) return false;
    }
    return true;
  }, [eventId, recipientEmail, tickets]);

  const submit = async () => {
    setSubmitting(true);
    setMessage("");
    setReservationToken("");

    try {
      const payload = {
        eventId: Number(eventId),
        recipientEmail: recipientEmail.trim(),
        idempotencyKey: idempotencyKey.trim() || undefined,
        tickets: tickets.map((t) => ({
          firstName: t.firstName.trim(),
          lastName: t.lastName.trim(),
          dateOfBirth: t.dateOfBirth, // yyyy-MM-dd
          ticketType: t.ticketType,
        })),
      };

      // axios client already adds auth + csrf
      const { data } = await api.post("/api/admin/comp/issue", payload);

      setStatus("SENT");
      setMessage("Tickets issued and emailed to recipient.");
      setReservationToken(data?.reservationToken || "");
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to issue tickets.";
      setMessage(String(msg));
      setStatus("DRAFT");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container py-6 pb-24 space-y-4">
      {/* Header */}
      <header className="card">
        <div className="md:flex md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl">
              Issue Complimentary Tickets
            </h1>
            <p className="mt-1 text-slate-300">
              MASTER-only tool. Sends tickets to an email you provide, but
              stores the tickets on your admin account.
            </p>
          </div>
          <Badge tone={status === "SENT" ? "success" : "warning"}>
            {status === "SENT" ? "ISSUED" : "DRAFT"}
          </Badge>
        </div>
      </header>

      {/* Form card */}
      <section className="card space-y-5">
        <div>
          <h2 className="text-white font-semibold text-lg">Event & Recipient</h2>
          <p className="text-slate-300 text-sm">
            Choose the event, then who should receive the ticket email.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {/* Event */}
          <label className="block">
            <div className="label">Event *</div>
            <select
              className="input w-full"
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              disabled={loadingEvents}
            >
              <option value="">Select event…</option>
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.title} — {ev.date}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-400">
              Inventory will still be checked for this event.
            </p>
          </label>

          {/* Recipient email */}
          <label className="block">
            <div className="label">Recipient email *</div>
            <input
              type="email"
              className="input w-full"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="friend@example.com"
            />
            <p className="mt-1 text-xs text-slate-400">
              Tickets are emailed here but owned by you (the MASTER user).
            </p>
          </label>

          {/* Idempotency */}
          <label className="block md:col-span-2">
            <div className="label">Idempotency key (optional)</div>
            <input
              className="input w-full"
              value={idempotencyKey}
              onChange={(e) => setIdempotencyKey(e.target.value)}
              placeholder="comp-evt-123-2025-10-30-admin"
            />
            <p className="mt-1 text-xs text-slate-400">
              If you submit twice with the same key, the backend won’t double
              issue.
            </p>
          </label>
        </div>

        {/* Ticket holders */}
        <div className="border-t border-white/10 pt-3 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-white font-semibold text-base">
                Ticket holders ({tickets.length}/{MAX_COMP_TICKETS})
              </h3>
              <p className="text-slate-400 text-sm">
                Each ticket must have a name and date of birth. 18+ enforced.
              </p>
            </div>
            <button
              type="button"
              className="btn-secondary disabled:opacity-50"
              onClick={addHolder}
              disabled={!canAddMore}
            >
              Add holder
            </button>
          </div>

          <div className="grid gap-3">
            {tickets.map((t, i) => (
              <div
                key={i}
                className="rounded-xl border border-white/10 bg-white/5 p-4"
              >
                <div className="flex items-center justify-between mb-3 gap-3">
                  <div className="font-semibold text-white">
                    Holder #{i + 1}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeHolder(i)}
                    className="text-sm text-red-300 hover:text-red-200"
                    disabled={tickets.length === 1}
                  >
                    Remove
                  </button>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <label className="block">
                    <div className="label">First name *</div>
                    <input
                      className="input w-full"
                      value={t.firstName}
                      onChange={(e) =>
                        updateHolder(i, "firstName", e.target.value)
                      }
                      placeholder="Jane"
                    />
                  </label>
                  <label className="block">
                    <div className="label">Last name *</div>
                    <input
                      className="input w-full"
                      value={t.lastName}
                      onChange={(e) =>
                        updateHolder(i, "lastName", e.target.value)
                      }
                      placeholder="Doe"
                    />
                  </label>
                  <label className="block">
                    <div className="label">Date of birth *</div>
                    <input
                      type="date"
                      className="input w-full"
                      value={t.dateOfBirth}
                      onChange={(e) =>
                        updateHolder(i, "dateOfBirth", e.target.value)
                      }
                    />
                  </label>
                  <label className="block">
                    <div className="label">Ticket type *</div>
                    <select
                      className="input w-full"
                      value={t.ticketType}
                      onChange={(e) =>
                        updateHolder(i, "ticketType", e.target.value)
                      }
                    >
                      <option value="STANDARD">STANDARD</option>
                      <option value="VIP">VIP</option>
                    </select>
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Messages */}
        {message && (
          <div
            className={`rounded-xl px-3 py-2 text-sm ${
              status === "SENT"
                ? "bg-emerald-500/10 text-emerald-100 border border-emerald-500/20"
                : "bg-red-500/10 text-red-100 border border-red-500/20"
            }`}
          >
            {message}
            {reservationToken && (
              <div className="mt-1 text-xs text-slate-200">
                Token: <code className="font-mono">{reservationToken}</code>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Sticky actions */}
      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-white/10 bg-[var(--bg)]/80 backdrop-blur">
        <div className="container py-3 flex items-center justify-between gap-3">
          <span className="text-slate-300 text-sm">
            {status === "SENT"
              ? "Tickets have been issued."
              : "Fill in the event, recipient and 1–10 ticket holders."}
          </span>
          <div className="flex gap-2">
            <button
              className="btn-secondary"
              type="button"
              onClick={() => navigate("/admin")}
            >
              Back
            </button>
            <button
              className="btn-primary"
              type="button"
              onClick={submit}
              disabled={!isValid || submitting}
            >
              {submitting ? "Issuing…" : "Issue tickets"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
